import { Fragment, useMemo, useState, useCallback } from 'react';
import { Button } from '@mui/material';
import useApi from 'shared/hooks/api';
import api from 'utils/api';
import { PageLoader, PageError, Modal } from 'components';

import DetailsModal from 'views/admin/components/DetailsModal';
import NewModal from './newModal';
import { DOLPHIN_API } from 'shared/constants/apis';
import BasicTable from 'components/BasicTable';
import { userTableColumns } from './tableColumns';
import { ConfirmModal } from 'shared/modals/confirmModal';
import { useSnackbar } from 'notistack';

const IWGDashboardUsers = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [userDetailsModalOpen, setUserDetailsModalOpen] = useState(false);
  const [userIdSelected, setUserIdSelected] = useState(-1);
  const [userDataSelected, setUserDataSelected] = useState({});
  const [userNewModalOpen, setUserNewModalOpen] = useState(false);
  const [userDeleteModalOpen, setUserDeleteModalOpen] = useState(false);

  const getChangableUserData = useCallback((user) => {
    return {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      userType: user.userType,
      preferredLanguage: user.preferredLanguage,
      preferredTimezone: user.preferredTimezone,
    };
  }, []);

  const handleOpenUserDetails = useCallback(
    (user) => {
      setUserIdSelected(user.id);
      setUserDataSelected(() => ({
        ...getChangableUserData(user),
      }));
      setUserDetailsModalOpen(true);
    },
    [getChangableUserData]
  );

  const handleOpenDeleteUser = useCallback(
    (user) => {
      setUserIdSelected(user.id);
      setUserDataSelected(() => ({
        ...getChangableUserData(user),
      }));
      setUserDeleteModalOpen(true);
    },
    [getChangableUserData]
  );

  const columns = useMemo(
    () => userTableColumns(handleOpenUserDetails, handleOpenDeleteUser),
    [handleOpenUserDetails, handleOpenDeleteUser]
  );

  const [{ data, error }, fetchUsers] = useApi.useGet(
    DOLPHIN_API.ADMIN_USERS,
    {}
  );
  if (!data) return <PageLoader />;
  if (error)
    return (
      <PageError
        errorCode={error.response.status}
        customErrorMessage={error.response.data.message}
        customErrorDescription={error.response.data.description}
      />
    );

  const handleUpdateUser = async (userId, updatedUserJSON) => {
    const updatedUserData = JSON.parse(updatedUserJSON);

    await api.put(DOLPHIN_API.ADMIN_USERS, {
      data: {
        ids: [userId],
        updatedData: [updatedUserData],
      },
    });
    enqueueSnackbar('User updated successfully', {
      variant: 'success',
    });
    await fetchUsers();
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(DOLPHIN_API.ADMIN_USERS, {
        data: {
          ids: [userId],
        },
      });

      enqueueSnackbar('User deleted successfully', {
        variant: 'success',
      });
      await fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      // Show the complete error message to the user
      enqueueSnackbar(`Error deleting user: ${error.message || error}`, {
        variant: 'error',
      });
    }
    setUserDeleteModalOpen(false);
  };

  data.sort((user1, user2) => {
    if (user1.id > user2.id) return 1;
    if (user1.id < user2.id) return -1;
    return 0;
  });

  const tableData = data.map((user) => {
    return {
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      username: user.username,
      details: user,
      delete: user,
    };
  });

  return (
    <Fragment>
      <BasicTable
        columns={columns}
        data={tableData}
        handleRefreshClick={fetchUsers}
        tableTitle="Users"
        rowsPerPageOptions={[10, 20, 50, 100]}
        initialPageSize={50}
        customToolbar={
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              setUserNewModalOpen(true);
            }}>
            Add New User
          </Button>
        }
      />

      {/* This is irregular */}
      <Modal
        isOpen={userNewModalOpen}
        width="700px"
        withCloseIcon={true}
        onClose={() => setUserNewModalOpen(false)}
        renderContent={(modal) => (
          <NewModal close={() => modal.close()} fetchUsers={fetchUsers} />
        )}
      />

      <Modal
        isOpen={userDetailsModalOpen}
        width="700px"
        withCloseIcon={true}
        onClose={() => setUserDetailsModalOpen(false)}
        renderContent={() => (
          <DetailsModal
            dataType="User"
            dataId={userIdSelected}
            data={userDataSelected}
            updateData={handleUpdateUser}
          />
        )}
      />

      <ConfirmModal
        isOpened={userDeleteModalOpen}
        onConfirm={() => handleDeleteUser(userIdSelected)}
        onCancel={() => setUserDeleteModalOpen(false)}
        title={`Do you want to DELETE User: ${userDataSelected?.firstName} ${userDataSelected?.lastName}`}
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </Fragment>
  );
};

export default IWGDashboardUsers;
